import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FreshdeskResponder, FreshdeskFeedbackListItem, FreshdeskFeedbackListFilters } from 'library/types';

const useFreshdeskFeedbackReport = (filters: FreshdeskFeedbackListFilters, enabled: boolean) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const feedbackRequestId = useRef(0);
  const feedbackController = useRef<AbortController | null>(null);
  const respondersController = useRef<AbortController | null>(null);
  const successfulFilterKey = useRef<string | null>(null);

  const [items, setItems] = useState<FreshdeskFeedbackListItem[]>([]);
  const [responders, setResponders] = useState<FreshdeskResponder[]>([]);
  const [loading, setLoading] = useState(false);
  const [respondersLoading, setRespondersLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [respondersHasError, setRespondersHasError] = useState(false);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);

  const loadFeedback = useCallback(async () => {
    if (!enabled) return;

    feedbackController.current?.abort();
    const controller = new AbortController();
    const requestId = feedbackRequestId.current + 1;
    const filterKey = JSON.stringify(filters);
    feedbackController.current = controller;
    feedbackRequestId.current = requestId;

    try {
      setLoading(true);
      setHasError(false);
      if (successfulFilterKey.current !== filterKey) {
        setItems([]);
        setLoadedAt(null);
      }

      const response = await apiService.getFreshdeskFeedbackList(filters, controller.signal);
      if (!response.isSuccess) {
        throw new Error(response.message || intl.formatMessage({ id: 'freshdeskFeedback.loadError' }));
      }

      if (feedbackRequestId.current === requestId) {
        setItems(response.data || []);
        setLoadedAt(new Date().toISOString());
        successfulFilterKey.current = filterKey;
      }
    } catch (error) {
      if (controller.signal.aborted || feedbackRequestId.current !== requestId) return;

      const message =
        error instanceof Error && error.message
          ? error.message
          : intl.formatMessage({ id: 'freshdeskFeedback.loadError' });

      setHasError(true);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      if (!controller.signal.aborted && feedbackRequestId.current === requestId) setLoading(false);
    }
  }, [enabled, enqueueSnackbar, filters, intl]);

  useEffect(() => {
    loadFeedback();

    return () => feedbackController.current?.abort();
  }, [loadFeedback]);

  const loadResponders = useCallback(async () => {
    if (!enabled) return;

    respondersController.current?.abort();
    const controller = new AbortController();
    respondersController.current = controller;

    try {
      setRespondersLoading(true);
      setRespondersHasError(false);
      const response = await apiService.getFreshdeskResponders(controller.signal);

      if (!response.isSuccess) {
        throw new Error(response.message || intl.formatMessage({ id: 'freshdeskFeedback.respondersLoadError' }));
      }

      setResponders(response.data || []);
    } catch (error) {
      if (controller.signal.aborted) return;

      const message =
        error instanceof Error && error.message
          ? error.message
          : intl.formatMessage({ id: 'freshdeskFeedback.respondersLoadError' });

      setRespondersHasError(true);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      if (!controller.signal.aborted) setRespondersLoading(false);
    }
  }, [enabled, enqueueSnackbar, intl]);

  useEffect(() => {
    loadResponders();

    return () => respondersController.current?.abort();
  }, [loadResponders]);

  const refreshAll = useCallback(() => {
    loadFeedback();
    loadResponders();
  }, [loadFeedback, loadResponders]);

  return {
    items,
    responders,
    loading,
    initialLoading: loading && !loadedAt,
    respondersLoading,
    hasError,
    respondersHasError,
    loadedAt,
    refresh: loadFeedback,
    refreshResponders: loadResponders,
    refreshAll,
  };
};

export default useFreshdeskFeedbackReport;
